// src/components/SopralluoghiDellaRC.js
// ═══════════════════════════════════════════════════════════════════════════
// SOPRALLUOGHI DI UNA RICHIESTA COMMERCIALE (ottobre 2026, W416)
// ═══════════════════════════════════════════════════════════════════════════
// Dentro la Richiesta Commerciale: i sopralluoghi nati da lei (campo rcId del sopralluogo) e
// «Nuovo sopralluogo da questa richiesta», con cliente e RC già scelti. Se la RC è già di una
// commessa, il sopralluogo nasce nella commessa; altrimenti è un sopralluogo senza commessa
// (permesso «sopralluoghi-senza-commessa»).
// ═══════════════════════════════════════════════════════════════════════════
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { collection, query, where, onSnapshot, doc, getDoc } from "firebase/firestore";
import { db } from "../firebase";
import { useAuth } from "../contexts/AuthContext";
import { useCanSee } from "../hooks/useCanSee";
import { SopralluogoModal } from "./SopralluogoTab";
import { senzaCommessa, PERMESSO_SENZA_COMMESSA } from "../utils/sopralluoghi";
import { stampaSopralluogo } from "../utils/stampaSopralluogo";

export default function SopralluoghiDellaRC({ rc, orgId }) {
  const { isOrgAdmin, isPlatformOwner } = useAuth();
  const canSee = useCanSee();
  const [elenco, setElenco] = useState([]);
  const [nuovo, setNuovo] = useState(false);
  const [orgInfo, setOrgInfo] = useState(null);
  const [commessa, setCommessa] = useState(null);
  const rcId = rc?.id || "";
  useEffect(() => {
    if (!rcId || !orgId) return undefined;
    return onSnapshot(query(collection(db, "sopralluoghi"), where("orgId", "==", orgId), where("rcId", "==", rcId)),
      s => setElenco(s.docs.map(d => ({ id: d.id, ...d.data() })).sort((a, b) => String(b.data || "").localeCompare(String(a.data || "")))),
      () => setElenco([]));
  }, [rcId, orgId]);
  useEffect(() => {
    if (!nuovo || !orgId) return;
    getDoc(doc(db, "organizations", orgId)).then(s => setOrgInfo(s.exists() ? s.data() : null)).catch(() => {});
    if (rc?.projectId) getDoc(doc(db, "projects", rc.projectId)).then(s => setCommessa(s.exists() ? { id: s.id, ...s.data() } : null)).catch(() => setCommessa(null));
  }, [nuovo, orgId, rc?.projectId]);
  if (!rcId) return null;
  const inCommessa = !!rc.projectId;
  const puoCreare = inCommessa || isOrgAdmin || isPlatformOwner || canSee(PERMESSO_SENZA_COMMESSA);

  return (
    <div role="region" aria-label="Sopralluoghi di questa richiesta" style={{ border: "1px solid #bfdbfe", background: "#eff6ff", borderRadius: 9, padding: "9px 12px", margin: "10px 0" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <span style={{ fontSize: 12.5, fontWeight: 800, color: "#1d4ed8", flex: 1 }}>🔍 Sopralluoghi di questa richiesta ({elenco.length})</span>
        {puoCreare && (
          <button type="button" onClick={() => setNuovo(true)}
            style={{ fontSize: 11.5, fontWeight: 700, padding: "4px 10px", borderRadius: 7, border: "none", background: "#2563eb", color: "#fff", cursor: "pointer" }}>
            + Nuovo sopralluogo da questa richiesta
          </button>
        )}
      </div>
      {elenco.map(s => (
        <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", fontSize: 12, color: "#374151", padding: "5px 0", borderTop: "1px dashed #bfdbfe", marginTop: 5 }}>
          <b style={{ fontFamily: "monospace", color: "#1d4ed8" }}>{s.numero || "—"}</b>
          <span>{s.data ? new Date(s.data + "T00:00:00").toLocaleDateString("it-IT") : "—"}</span>
          <span style={{ flex: 1, minWidth: 140, color: "#6b7280" }}>{s.motivoSopralluogo || ""}</span>
          <span style={{ fontSize: 10.5, padding: "1px 7px", borderRadius: 10, fontWeight: 700, ...(senzaCommessa(s) ? { background: "#fffbeb", color: "#92400e" } : { background: "#ede9fe", color: "#5b21b6" }) }}>
            {senzaCommessa(s) ? "Senza commessa" : (s.codiceCommessa || s.projectName || "In commessa")}
          </span>
          <button type="button" onClick={() => stampaSopralluogo(s, null, s.projectName || "")}
            style={{ fontSize: 11, padding: "2px 8px", border: "1px solid #bfdbfe", borderRadius: 6, background: "#fff", color: "#2563eb", cursor: "pointer" }}>PDF</button>
        </div>
      ))}
      {/* il verbale si apre sopra la finestra della Richiesta, non dentro (portale) */}
      {nuovo && createPortal(
        <div style={{ position: "relative", zIndex: 1100 }}><SopralluogoModal sopralluogo={null} orgId={orgId} orgInfo={orgInfo}
          projectId={inCommessa ? rc.projectId : ""} projectName={inCommessa ? (rc.projectName || commessa?.name || "") : ""}
          codiceCommessa={inCommessa ? (rc.codiceCommessa || commessa?.codiceCommessa || "") : ""}
          clienteNome={inCommessa ? (rc.clienteNome || "") : ""} commessa={commessa}
          predefiniti={{ rcId, rcNumero: rc.numero || "", clienteId: rc.clienteId || "", nomeCliente: rc.clienteNome || "", potenziale: !rc.clienteId }}
          onClose={() => setNuovo(false)} /></div>, document.body
      )}
    </div>
  );
}
